<template>
  <div class="quill-editor-container">
    <div class="editor-note">
      <div class="note-icon">ℹ️</div>
      <div class="note-text">
        <strong>Image Upload:</strong> To add images, use the image button in the toolbar above. 
        Copying and pasting images directly into the editor does not work.
      </div>
    </div>
    <QuillEditor
      ref="quillEditor"
      v-model:content="editorContent"
      content-type="html"
      :options="editorOptions"
      @update:content="handleContentUpdate"
      @ready="onEditorReady"
    />

    <!-- Image URL Modal -->
    <div v-if="showImageModal" class="image-modal-overlay" @click="closeImageModal">
      <div class="image-modal" @click.stop>
        <div class="image-modal-header">
          <h3>Add Image by URL</h3>
          <button class="close-btn" @click="closeImageModal">&times;</button>
        </div>
        
        <div class="image-modal-body">
          <p class="tab-description">Enter the URL of an image from the web</p>
          <div class="url-input-group">
            <input 
              v-model="imageUrl"
              type="url" 
              placeholder="https://example.com/image.jpg"
              class="url-input"
              @keyup.enter="insertImageFromUrl"
              ref="urlInput"
            >
       
          </div>
          
          <!-- Image Preview -->
          <div v-if="previewUrl" class="image-preview">
            <img :src="previewUrl" alt="Preview" @load="imageLoaded = true" @error="imageError = true">
            <p v-if="imageError" class="error-text">Failed to load image. Please check the URL.</p>
          </div>

          <div class="url-actions">
            <button 
              class="cancel-btn"
              @click="closeImageModal"
            >
              Cancel
            </button>
            <button 
              class="insert-btn"
              @click="insertImageFromUrl"
              :disabled="!imageUrl || imageError"
            >
              Insert Image
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Embed Modal -->
    <div v-if="showVideoModal" class="image-modal-overlay" @click="closeVideoModal">
      <div class="image-modal" @click.stop>
        <div class="image-modal-header">
          <h3>Add Video Embed</h3>
          <button class="close-btn" @click="closeVideoModal">&times;</button>
        </div>
        
        <div class="image-modal-body">
          <p class="tab-description">Enter a YouTube, Vimeo, or embed URL</p>
          <div class="url-input-group">
            <input 
              v-model="videoUrl"
              type="url" 
              placeholder="YouTube, Vimeo, or embed URL"
              class="url-input"
              @keyup.enter="insertVideoEmbed"
              ref="videoUrlInput"
            >
          </div>
          
          <!-- Video Preview -->
          <div v-if="videoPreviewHtml" class="video-preview">
            <div v-html="videoPreviewHtml"></div>
          </div>

          <div class="url-actions">
            <button 
              class="cancel-btn"
              @click="closeVideoModal"
            >
              Cancel
            </button>
            <button 
              class="insert-btn"
              @click="insertVideoEmbed"
              :disabled="!videoUrl"
            >
              Insert Video
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Hyperlink Modal -->
    <div v-if="showLinkModal" class="image-modal-overlay" @click="closeLinkModal">
      <div class="image-modal" @click.stop>
        <div class="image-modal-header">
          <h3>Add/Edit Hyperlink</h3>
          <button class="close-btn" @click="closeLinkModal">&times;</button>
        </div>
        
        <div class="image-modal-body">
          <div class="form-group">
            <label for="linkUrl">URL</label>
            <input 
              id="linkUrl"
              v-model="linkUrl"
              type="url" 
              placeholder="https://example.com"
              class="url-input"
              @keyup.enter="insertHyperlink"
              ref="linkUrlInput"
            >
          </div>

          <div class="form-group">
            <label for="linkText">Display Text</label>
            <input 
              id="linkText"
              v-model="linkText"
              type="text" 
              placeholder="Link text"
              class="url-input"
              @keyup.enter="insertHyperlink"
            >
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input 
                v-model="linkOpenInNewTab"
                type="checkbox"
              >
              <span class="checkbox-text">Open in new tab</span>
            </label>
          </div>

          <div class="url-actions">
            <button 
              class="cancel-btn"
              @click="closeLinkModal"
            >
              Cancel
            </button>
            <button 
              class="insert-btn"
              @click="insertHyperlink"
              :disabled="!linkUrl"
            >
              Insert Link
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { QuillEditor, Quill } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import QuillResizeImage from 'quill-resize-image';
import htmlEditButton from 'quill-html-edit-button';

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
});

// Emits
const emit = defineEmits(['update:modelValue']);

// Refs
const quillEditor = ref(null);

// Reactive data
const editorContent = ref(props.modelValue);
const savedSelection = ref(null);
const showImageModal = ref(false);
const imageUrl = ref('');
const previewUrl = ref('');
const imageLoaded = ref(false);
const imageError = ref(false);
const currentRange = ref(null);
const urlInput = ref(null);

// Video modal data
const showVideoModal = ref(false);
const videoUrl = ref('');
const videoPreviewHtml = ref('');
const videoUrlInput = ref(null);

// Hyperlink modal data
const showLinkModal = ref(false);
const linkUrl = ref('');
const linkText = ref('');
const linkOpenInNewTab = ref(false);
const linkUrlInput = ref(null);
const selectedLinkText = ref('');

// Register the resize module
Quill.register('modules/resize', QuillResizeImage);

// Register the HTML edit button module
Quill.register('modules/htmlEditButton', htmlEditButton);

// The site sends `Referrer-Policy: no-referrer`, so a cross-origin request from
// one of its pages carries no Referer. YouTube's embedded player requires one
// and refuses to start without it, showing "Video player configuration error.
// Error 153". Opting back in on the iframe itself overrides the document policy
// for that frame only, leaving the rest of the page unreferred.
//
// This has to happen in the blot: Quill rebuilds the iframe from its Delta on
// every render and keeps only src/frameborder/allowfullscreen, so an attribute
// on the HTML handed to dangerouslyPasteHTML is discarded.
const QuillVideo = Quill.import('formats/video');

class ReferrerSafeVideo extends QuillVideo {
  static create(value) {
    const node = super.create(value);
    node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    return node;
  }
}

Quill.register(ReferrerSafeVideo, true);

// Computed
const editorOptions = computed(() => ({
  theme: 'snow',
  placeholder: 'Enter content...',
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image', 'video']
    ],
    resize: {
      locale: {
        center: 'center',
      },
    },
    htmlEditButton: {
      debug: false, // logging, default:false
      msg: "Edit the content in HTML format", //Custom message to display in the editor, default: Edit HTML here, when you click "OK" the quill editor's contents will be replaced
      okText: "Ok", // Text to display in the OK button, default: Ok,
      cancelText: "Cancel", // Text to display in the cancel button, default: Cancel
      buttonHTML: "&lt;&gt;", // Text to display in the toolbar button, default: <>
      buttonTitle: "Show HTML source", // Text to display as the tooltip for the toolbar button, default: Show HTML source
      syntax: false, // Show the HTML with syntax highlighting. Requires highlightjs on window.hljs (similar to Quill itself), default: false
      prependSelector: 'div#myelement', // a string used to select where you want to insert the overlayContainer, default: null (appends to body),
      editorModules: {} // The default mod
    },
  },
}));

// Watchers
watch(() => props.modelValue, (newVal) => {
  if (newVal !== editorContent.value) {
    editorContent.value = newVal;
    // Force the Quill editor to update its content
    if (quillEditor.value && quillEditor.value.getQuill) {
      const quill = quillEditor.value.getQuill();
      if (quill && quill.root.innerHTML !== newVal) {
        quill.clipboard.dangerouslyPasteHTML(newVal || '');
      }
    }
  }
}, { immediate: true });

// Watch for video URL changes to show preview
watch(videoUrl, (newUrl) => {
  if (newUrl) {
    const embedUrl = convertToEmbedUrl(newUrl);
    let previewHtml;
    if (embedUrl.includes('youtube.com/embed')) {
      previewHtml = `<iframe src="${embedUrl}" width="100%" height="200" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`;
    } else {
      previewHtml = `<iframe src="${embedUrl}" width="100%" height="200" frameborder="0" allowfullscreen></iframe>`;
    }
    videoPreviewHtml.value = previewHtml;
  } else {
    videoPreviewHtml.value = '';
  }
});

// Methods
const handleContentUpdate = (content) => {
  emit('update:modelValue', content);
};

const getQuillInstance = () => {
  return quillEditor.value?.getQuill();
};

const setContent = (content) => {
  const quill = getQuillInstance();
  if (quill) {
    quill.clipboard.dangerouslyPasteHTML(content || '');
  }
  editorContent.value = content;
};

const restoreSelection = () => {
  const quill = getQuillInstance();
  if (quill && savedSelection.value) {
    quill.setSelection(savedSelection.value);
  }
};

const saveSelection = () => {
  const quill = getQuillInstance();
  if (quill) {
    savedSelection.value = quill.getSelection();
  }
};

// Custom image handler to show modal for URL input
const imageHandler = () => {
  const quill = getQuillInstance();
  currentRange.value = quill.getSelection(true);
  showImageModal.value = true;
  
  // Reset modal state
  imageUrl.value = '';
  previewUrl.value = '';
  imageLoaded.value = false;
  imageError.value = false;
  
  // Focus the URL input after modal opens
  nextTick(() => {
    if (urlInput.value) {
      urlInput.value.focus();
    }
  });
};

// Modal methods
const closeImageModal = () => {
  showImageModal.value = false;
  currentRange.value = null;
};

const previewImage = () => {
  if (imageUrl.value) {
    previewUrl.value = imageUrl.value;
    imageLoaded.value = false;
    imageError.value = false;
  }
};

const insertImageFromUrl = () => {
  if (!imageUrl.value) {
    alert('Please enter an image URL');
    return;
  }

  try {
    new URL(imageUrl.value); // Validate URL format
  } catch {
    alert('Please enter a valid URL');
    return;
  }

  const quill = getQuillInstance();
  if (currentRange.value) {
    quill.insertEmbed(currentRange.value.index, 'image', imageUrl.value);
    quill.setSelection(currentRange.value.index + 1);
  }
  
  closeImageModal();
};

// Video modal methods
const closeVideoModal = () => {
  showVideoModal.value = false;
  videoUrl.value = '';
  videoPreviewHtml.value = '';
  currentRange.value = null;
};

const convertToEmbedUrl = (url) => {
  // Convert YouTube URLs to embed format
  if (url.includes('youtube.com/watch')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    if (videoId) {
      // Add parameters to prevent embedding restrictions
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    }
  }
  
  // Convert YouTube short URLs to embed format
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    if (videoId) {
      // Add parameters to prevent embedding restrictions
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    }
  }
  
  // Convert Vimeo URLs to embed format
  if (url.includes('vimeo.com/') && !url.includes('player.vimeo.com')) {
    const videoId = url.split('vimeo.com/')[1]?.split('/')[0]?.split('?')[0];
    if (videoId && !isNaN(videoId)) {
      return `https://player.vimeo.com/video/${videoId}`;
    }
  }
  
  // Return the URL as-is if no conversion needed (already embed format or other video services)
  return url;
};

const insertVideoEmbed = () => {
  if (!videoUrl.value || !currentRange.value) return;
  
  const embedUrl = convertToEmbedUrl(videoUrl.value);
  const quill = getQuillInstance();
  
  // Create iframe HTML with proper YouTube attributes
  let iframeHtml;
  if (embedUrl.includes('youtube.com/embed')) {
    iframeHtml = `<iframe src="${embedUrl}" width="100%" height="500" title="YouTube video player" frameborder="0" allowfullscreen></iframe>`;
  } else {
    // For other video services, use simpler iframe
    iframeHtml = `<iframe src="${embedUrl}" width="100%" height="500" frameborder="0" allowfullscreen></iframe>`;
  }
  
  // Insert the iframe
  quill.clipboard.dangerouslyPasteHTML(currentRange.value.index, iframeHtml);
  quill.setSelection(currentRange.value.index + 1);
  
  closeVideoModal();
};

// Custom video handler to show modal
const videoHandler = () => {
  const quill = getQuillInstance();
  currentRange.value = quill.getSelection(true);
  showVideoModal.value = true;
  
  // Focus the URL input after modal opens
  nextTick(() => {
    if (videoUrlInput.value) {
      videoUrlInput.value.focus();
    }
  });
};

// Hyperlink modal methods
const closeLinkModal = () => {
  showLinkModal.value = false;
  linkUrl.value = '';
  linkText.value = '';
  linkOpenInNewTab.value = false;
  selectedLinkText.value = '';
  currentRange.value = null;
};

const insertHyperlink = () => {
  if (!linkUrl.value) {
    alert('Please enter a URL');
    return;
  }

  try {
    new URL(linkUrl.value); // Validate URL format
  } catch {
    alert('Please enter a valid URL');
    return;
  }

  const quill = getQuillInstance();
  if (!currentRange.value) return;

  // Determine what text to use for the link
  let textToUse = linkText.value || selectedLinkText.value || linkUrl.value;
  
  // If there's selected text and no custom text provided, use the selected text
  if (currentRange.value.length > 0 && !linkText.value) {
    textToUse = selectedLinkText.value;
  }

  // If we have selected text, format it as a link
  if (currentRange.value.length > 0) {
    quill.formatText(currentRange.value.index, currentRange.value.length, 'link', linkUrl.value);
    
    // Add target and rel attributes if needed
    if (linkOpenInNewTab.value) {
      quill.formatText(currentRange.value.index, currentRange.value.length, 'target', '_blank');
      quill.formatText(currentRange.value.index, currentRange.value.length, 'rel', 'noopener noreferrer');
    }
  } else {
    // Insert new link text
    quill.insertText(currentRange.value.index, textToUse);
    quill.formatText(currentRange.value.index, textToUse.length, 'link', linkUrl.value);
    
    // Add target and rel attributes if needed
    if (linkOpenInNewTab.value) {
      quill.formatText(currentRange.value.index, textToUse.length, 'target', '_blank');
      quill.formatText(currentRange.value.index, textToUse.length, 'rel', 'noopener noreferrer');
    }
    
    quill.setSelection(currentRange.value.index + textToUse.length);
  }
  
  closeLinkModal();
};

// Custom link handler to show modal
const linkHandler = () => {
  const quill = getQuillInstance();
  const selection = quill.getSelection(true);
  currentRange.value = selection;

  // Check if there's selected text
  if (selection && selection.length > 0) {
    selectedLinkText.value = quill.getText(selection.index, selection.length);
    linkText.value = selectedLinkText.value;
    
    // Check if the selected text is already a link
    const format = quill.getFormat(selection.index, selection.length);
    if (format.link) {
      // Handle both string and object formats for the link
      if (typeof format.link === 'string') {
        linkUrl.value = format.link;
      } else if (typeof format.link === 'object' && format.link.href) {
        linkUrl.value = format.link.href;
        linkOpenInNewTab.value = format.link.target === '_blank';
      } else {
        linkUrl.value = '';
      }
      
      // Check target from format if not already set
      if (format.target === '_blank') {
        linkOpenInNewTab.value = true;
      }
    }
  } else {
    selectedLinkText.value = '';
    linkText.value = '';
    linkUrl.value = '';
    linkOpenInNewTab.value = false;
  }
  
  showLinkModal.value = true;
  
  // Focus the URL input after modal opens
  nextTick(() => {
    if (linkUrlInput.value) {
      linkUrlInput.value.focus();
    }
  });
};

// Expose methods for parent components
defineExpose({
  getQuillInstance,
  setContent,
  restoreSelection,
  saveSelection,
});

const onEditorReady = (quill) => {
  console.log('Quill editor ready:', quill);
  
  // Ensure the editor has the correct content when it's ready
  if (props.modelValue && props.modelValue !== quill.root.innerHTML) {
    quill.clipboard.dangerouslyPasteHTML(props.modelValue);
  }

  // Custom handlers to prevent default behavior
  const toolbar = quill.getModule('toolbar');
  if (toolbar) {
    toolbar.addHandler('image', imageHandler);
    toolbar.addHandler('video', videoHandler);
    toolbar.addHandler('link', linkHandler);
  }

  // Add selection preservation logic
  quill.on('selection-change', (range, oldRange, source) => {
    if (range) {
      savedSelection.value = range;
    }
  });

  // Handle toolbar focus issues
  if (toolbar && toolbar.container) {
    // Prevent all toolbar interactions from stealing focus
    const toolbarButtons = toolbar.container.querySelectorAll('button, select, .ql-picker');
    toolbarButtons.forEach(element => {
      // Handle mousedown on buttons and selects
      element.addEventListener('mousedown', (e) => {
        e.preventDefault();
        
        // For dropdowns, we need to save selection immediately
        setTimeout(() => {
          if (savedSelection.value) {
            quill.setSelection(savedSelection.value);
          }
        }, 0);
      });

      // Handle click events for dropdowns specifically
      element.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Restore selection after dropdown interaction
        setTimeout(() => {
          if (savedSelection.value) {
            quill.setSelection(savedSelection.value);
          }
        }, 10); // Slightly longer delay for dropdowns
      });
    });

    // Handle dropdown menu items specifically
    const handleDropdownItems = () => {
      const dropdownItems = toolbar.container.querySelectorAll('.ql-picker-options .ql-picker-item, .ql-color .ql-picker-item');
      dropdownItems.forEach(item => {
        item.addEventListener('mousedown', (e) => {
          e.preventDefault();
        });
        
        item.addEventListener('click', (e) => {
          // Allow the click to process but restore selection after
          setTimeout(() => {
            if (savedSelection.value) {
              quill.setSelection(savedSelection.value);
            }
          }, 50); // Longer delay to allow dropdown value to be processed
        });
      });
    };

    // Call initially and also set up a mutation observer for dynamically created dropdown items
    handleDropdownItems();
    
    // Watch for dropdown menus being added to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          handleDropdownItems();
        }
      });
    });
    
    observer.observe(toolbar.container, {
      childList: true,
      subtree: true
    });
  }
};

// Lifecycle
onMounted(() => {
  console.log('LHtmlEditor (Quill) mounted');
  // Ensure content is set after mounting if it exists
  if (props.modelValue) {
    editorContent.value = props.modelValue;
  }
});
</script>

<style scoped>
.quill-editor-container {
  position: relative;
}

/* Editor note styling */
.editor-note {
  display: flex;
  align-items: center;
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px 6px 0 0;
  padding: 8px 12px;
  font-size: 13px;
  color: #1565c0;
  margin-bottom: 0;
}

.note-icon {
  margin-right: 8px;
  font-size: 14px;
}

.note-text {
  line-height: 1.4;
}

.note-text strong {
  color: #0d47a1;
}

/* Adjust the toolbar to connect with the note */
:deep(.ql-toolbar) {
  border-top: none;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  border-bottom: none;
  border-radius: 0;
}

/* Custom Quill styling */
:deep(.ql-editor) {
  min-height: 200px;
  font-size: 14px;
  line-height: 1.42;
}

:deep(.ql-container) {
  border-bottom: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 6px 6px;
}

/* Ensure the editor has a consistent look */
:deep(.ql-snow .ql-editor) {
  padding: 12px 15px;
}

:deep(.ql-snow .ql-tooltip) {
  z-index: 1000;
  left: 0px !important;
}

/* Prevent toolbar buttons from stealing focus */
:deep(.ql-toolbar button) {
  outline: none;
}

:deep(.ql-toolbar button:focus) {
  outline: none;
}

/* Handle dropdown pickers */
:deep(.ql-toolbar .ql-picker) {
  outline: none;
}

:deep(.ql-toolbar .ql-picker:focus) {
  outline: none;
}

/* Dropdown items styling */
:deep(.ql-picker-options) {
  z-index: 1001;
}

:deep(.ql-picker-item) {
  outline: none;
}

:deep(.ql-picker-item:focus) {
  outline: none;
}

/* Color picker specific */
:deep(.ql-color .ql-picker-options) {
  z-index: 1001;
}

/* Ensure the editor maintains focus state visually */
:deep(.ql-editor:focus) {
  outline: none;
}

/* Keep toolbar button states visible */
:deep(.ql-toolbar .ql-active) {
  color: #06c !important;
}

/* HTML Edit Button Styling */
:deep(.ql-toolbar .ql-htmlEditButton) {
  width: auto;
  margin-left: 5px;
}

:deep(.ql-htmlEditButton svg) {
  width: 18px;
  height: 18px;
}

/* Modal styling for HTML editor */
:deep(.ql-html-overlayContainer) {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.ql-html-textContainer) {
  background: white;
  border-radius: 8px;
  padding: 20px;
  max-width: 90%;
  max-height: 90%;
  overflow: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

:deep(.ql-html-textArea) {
  width: 100%;
  height: 400px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  resize: vertical;
}

:deep(.ql-html-buttonContainer) {
  margin-top: 15px;
  text-align: right;
}

:deep(.ql-html-buttonContainer button) {
  margin-left: 10px;
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f8f9fa;
  cursor: pointer;
}

:deep(.ql-html-buttonContainer button:hover) {
  background: #e9ecef;
}

:deep(.ql-html-buttonContainer .ql-html-buttonCancel) {
  background: #6c757d;
  color: white;
  border-color: #6c757d;
}

:deep(.ql-html-buttonContainer .ql-html-buttonOk) {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

/* Image Modal Styling */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.image-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
  border-bottom: 1px solid #eee;
  margin-bottom: 20px;
}

.image-modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-btn:hover {
  background: #f5f5f5;
}

.image-modal-body {
  padding: 0 20px 20px 20px;
}

.tab-description {
  color: #666;
  margin-bottom: 16px;
  font-size: 14px;
}

.url-input-group {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.url-input {
  flex: 1;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.url-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.preview-btn {
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}

.preview-btn:hover:not(:disabled) {
  background: #e9ecef;
}

.preview-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.image-preview {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  text-align: center;
  margin-bottom: 16px;
  background: #fafafa;
}

.image-preview img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
}

.error-text {
  color: #dc3545;
  font-size: 14px;
  margin: 8px 0 0 0;
}

.url-actions {
  text-align: right;
}

.insert-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  background: #007bff;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.insert-btn:hover:not(:disabled) {
  background: #0056b3;
}

.insert-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #6c757d;
}

/* Video modal styles */
.video-preview {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fafafa;
}

.video-preview iframe {
  max-width: 100%;
  border-radius: 4px;
}

/* Hyperlink modal specific styles */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group input[type="url"],
.form-group input[type="text"] {
  width: 100%;
  box-sizing: border-box;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  cursor: pointer;
  font-weight: normal !important;
  margin-bottom: 0 !important;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 8px;
  margin-bottom: 0;
}

.checkbox-text {
  font-size: 14px;
  color: #333;
}

.cancel-btn {
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  margin-right: 10px;
}

.cancel-btn:hover {
  background: #e9ecef;
}

/* Ensure all embedded iframes have 100% width by default */
.ql-editor iframe {
  width: 100% !important;
  max-width: 100%;
}
</style>